import { Box, Button, MenuItem, TextField } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { isOpenProjectStatus } from "../../lib/brain-api";
import type { Customer, Project, CustomerSpine, CustomerTickets } from "../../lib/brain-api";
import type { Thought } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { KpiCard, SectionCard, Icon, AreaChart, ExplainButton } from "../../mui";
import { AeronetCoreCardForCustomer } from "./CustomerIntegrationsTabMui";
import { Explain } from "../../agent/Explain";
import { relDate, fmtDate, toMs, growthSeries } from "../../mui/format";

const c = brainTokens.color;

type RecordFieldKind = "text" | "array" | "number" | "status";
interface RecordField {
  key: string;
  label: string;
  kind: RecordFieldKind;
  get: (c: Customer) => string;
  set?: (d: Customer, v: string) => Customer;
}

const joinArr = (a?: string[]) => (a && a.length ? a.join(", ") : "");
const splitArr = (v: string) => v.split(",").map((s) => s.trim()).filter(Boolean);

const FIELDS: RecordField[] = [
  { key: "status", label: "Status", kind: "status", get: (c) => c.status, set: (d, v) => ({ ...d, status: v as Customer["status"] }) },
  { key: "tier", label: "Tier", kind: "text", get: (c) => c.tier ?? "", set: (d, v) => ({ ...d, tier: v || undefined }) },
  { key: "csm", label: "Owner (CSM)", kind: "text", get: (c) => c.csm ?? "", set: (d, v) => ({ ...d, csm: v || undefined }) },
  { key: "primaryContact", label: "Primary contact", kind: "text", get: (c) => c.primaryContact ?? "", set: (d, v) => ({ ...d, primaryContact: v || undefined }) },
  { key: "product", label: "Products", kind: "array", get: (c) => joinArr(c.product), set: (d, v) => ({ ...d, product: splitArr(v) }) },
  { key: "modules", label: "Modules", kind: "array", get: (c) => joinArr(c.modules), set: (d, v) => ({ ...d, modules: splitArr(v) }) },
  { key: "domains", label: "Domains", kind: "array", get: (c) => joinArr(c.domains), set: (d, v) => ({ ...d, domains: splitArr(v) }) },
  { key: "location", label: "Location", kind: "text", get: (c) => c.location ?? "", set: (d, v) => ({ ...d, location: v || undefined }) },
  { key: "timezone", label: "Timezone", kind: "text", get: (c) => c.timezone ?? "", set: (d, v) => ({ ...d, timezone: v || undefined }) },
  { key: "summary", label: "Summary", kind: "text", get: (c) => c.summary ?? "", set: (d, v) => ({ ...d, summary: v || undefined }) },
  { key: "aliases", label: "Aliases", kind: "array", get: (c) => joinArr(c.aliases), set: (d, v) => ({ ...d, aliases: splitArr(v) }) },
  { key: "legalName", label: "Legal name", kind: "text", get: (c) => c.legalName ?? "", set: (d, v) => ({ ...d, legalName: v || undefined }) },
  { key: "crmCompanyId", label: "CRM company", kind: "number", get: (c) => (c.crmCompanyId != null ? `#${c.crmCompanyId}` : ""), set: (d, v) => ({ ...d, crmCompanyId: v.replace(/\D/g, "") ? Number(v.replace(/\D/g, "")) : undefined }) },
  { key: "startedAt", label: "Since", kind: "text", get: (c) => c.startedAt ?? "", set: (d, v) => ({ ...d, startedAt: v || undefined }) },
];

const cardGrid = { display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "14px", mb: "14px" } as const;

export function CustomerOverviewTabMui({
  customer,
  draft,
  setDraft,
  editing,
  onSave,
  onCancel,
  isAdmin,
  activity,
  projects,
  brief,
  spine,
  tickets,
}: {
  customer: Customer;
  draft: Customer | null;
  setDraft: (c: Customer) => void;
  editing: boolean;
  onSave: () => void;
  onCancel: () => void;
  isAdmin: boolean;
  activity: Thought[];
  projects: Project[];
  brief: { markdown?: string } | null;
  spine: CustomerSpine | null;
  tickets: CustomerTickets | null;
}) {
  const install = (customer.install ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  const appUrl = str(install.app_url) ?? str(install.appUrl) ?? str(install.url);
  const host = str(install.host) ?? str(install.server);
  const db = str(install.db) ?? str(install.database);
  const hasInstall = appUrl || host || db;

  const activeProjects = projects.filter((p) => isOpenProjectStatus(p.status));
  const avgComplete = activeProjects.length
    ? Math.round(activeProjects.reduce((s, p) => s + (p.completeness ?? 0), 0) / activeProjects.length)
    : 0;

  const lastTs = activity.reduce<number | null>((max, t) => {
    const ms = toMs(t.created_at);
    return ms != null && (max == null || ms > max) ? ms : max;
  }, null);

  const openWork = tickets?.counts.open ?? null;
  const totalTickets = tickets?.tickets.length ?? null;

  const series = growthSeries(activity);
  const editMode = editing && !!draft;

  const briefText = (brief?.markdown ?? "")
    .replace(/[#>*_`]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return (
    <Box>
      {/* 3 highlight cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", mb: "14px" }}>
        <KpiCard
          label="Open work"
          icon={<Icon name="calendar" size={14} stroke={1.8} />}
          value={openWork ?? "—"}
          unit={totalTickets != null ? `of ${totalTickets}` : ""}
          hint="open tickets"
          valueSize={32}
          labelAdornment={openWork != null ? <ExplainButton snapshot={{ label: "Open work", value: openWork, unit: "open tickets" }} /> : undefined}
        />
        <KpiCard
          label="Last touch"
          icon={<Icon name="clock" size={14} stroke={1.8} />}
          value={relDate(lastTs ?? undefined)}
          hint={lastTs ? fmtDate(lastTs) : "no activity yet"}
          valueSize={28}
          labelAdornment={lastTs ? <ExplainButton snapshot={{ label: "Last touch", value: relDate(lastTs) }} prompt={`What was the most recent activity for ${customer.name}, and is anything overdue? Cite sources.`} /> : undefined}
        />
        <KpiCard
          label="Active projects"
          icon={<Icon name="folder" size={14} stroke={1.8} />}
          value={activeProjects.length}
          unit={`of ${projects.length}`}
          hint={activeProjects.length ? `avg ${avgComplete}% complete` : "none active"}
          valueSize={32}
          labelAdornment={<ExplainButton snapshot={{ label: "Active projects", value: activeProjects.length }} />}
        />
      </Box>

      {/* Aeronet core stack — Roxen / FastAPI / React versions + live status */}
      <AeronetCoreCardForCustomer customerId={customer.customerId} />

      {/* install + CRM */}
      <Box sx={cardGrid}>
        <SectionCard
          label="Install · Live Instance"
          headerMb={18}
          actions={hasInstall ? <ExplainButton snapshot={{ label: "Install", value: appUrl ?? host ?? "instance" }} prompt={`What do we know about this install's history and health for ${customer.name}? Cite sources.`} /> : undefined}
        >
          {hasInstall ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "22px" }}>
              <InstallField label="App" value={appUrl} teal />
              <InstallField label="Host" value={host} />
              <InstallField label="Database" value={db} />
            </Box>
          ) : (
            <Box sx={{ fontSize: 13.5, color: c.muted2 }}>No install recorded.</Box>
          )}
        </SectionCard>
        <SectionCard label="Systems CRM">
          {spine?.linked && spine.systems.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {spine.systems.slice(0, 3).map((s) => (
                <Box key={s.id} sx={{ fontSize: 13, color: c.ink4 }}>
                  <Box component="span" sx={{ fontFamily: brainTokens.font.mono, color: c.tealInk2 }}>{s.domain ?? `#${s.id}`}</Box>
                  {s.distVersion ? ` · ${s.distVersion}` : ""}
                  {s.status ? ` · ${s.status}` : ""}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ fontSize: 13.5, color: c.muted2 }}>No matched install.</Box>
          )}
        </SectionCard>
      </Box>

      {/* record + projects mini */}
      <Box sx={cardGrid}>
        <SectionCard
          label="Record"
          actions={
            editMode ? (
              <>
                <Button size="small" onClick={onCancel} sx={{ color: c.ink3 }}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={onSave}>
                  Save
                </Button>
              </>
            ) : (
              <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: "#b0b6bc", letterSpacing: "0.04em" }}>
                {FIELDS.length} FIELDS{customer.updatedAt ? ` · UPDATED ${fmtDate(customer.updatedAt)}` : ""}
              </Box>
            )
          }
          headerMb={20}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "18px 26px" }}>
            {FIELDS.map((f) => {
              const value = f.get(customer);
              return (
                <Box key={f.key}>
                  <Box sx={{ fontSize: 11.5, color: c.faint, mb: "5px" }}>{f.label}</Box>
                  {editMode && f.set && draft ? (
                    f.kind === "status" ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={f.get(draft)}
                        onChange={(e) => setDraft(f.set!(draft, e.target.value))}
                      >
                        {["prospect", "active", "churned"].map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        value={f.get(draft)}
                        onChange={(e) => setDraft(f.set!(draft, e.target.value))}
                      />
                    )
                  ) : (
                    <Box sx={{ fontSize: 13.5, fontWeight: 500, color: value ? c.ink : c.placeholder }}>{value || "—"}</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </SectionCard>

        <SectionCard label={`Projects · ${projects.length}`}>
          {projects.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {projects.slice(0, 6).map((p) => {
                const pct = p.completeness ?? 0;
                const tag = p.priority ?? `${Math.round(pct)}%`;
                return (
                  <Box key={p.projectId}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px", gap: 1 }}>
                      <Box component={RouterLink} to={`/projects/${p.projectId}`} sx={{ fontSize: 13.5, fontWeight: 500, color: c.ink, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { color: c.tealInk } }}>
                        {p.name}
                      </Box>
                      <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint, flex: "none" }}>
                        {tag}
                      </Box>
                    </Box>
                    <Box sx={{ height: 5, borderRadius: "999px", background: "#eef1f3", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${Math.min(100, Math.max(0, pct))}%`, background: c.tealInk2, borderRadius: "999px" }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ fontSize: 13.5, color: c.muted2 }}>No projects yet.</Box>
          )}
        </SectionCard>
      </Box>

      {/* growth chart + file index */}
      <Box sx={cardGrid}>
        <SectionCard
          label="Knowledge Growth · 12 Months"
          labelIcon={<Icon name="chart" size={14} stroke={1.8} />}
          actions={<ExplainButton snapshot={{ label: "Knowledge growth", value: series.at(-1)?.value ?? 0 }} prompt={`What drove the knowledge growth for ${customer.name} over the last 12 months? Cite the sources of the biggest additions.`} />}
          headerMb={14}
        >
          <AreaChart values={series.map((s) => s.value)} leftLabel={series[0]?.label} rightLabel={series.length ? `${series[series.length - 1].label} · ${series[series.length - 1].value}` : undefined} />
        </SectionCard>
        <SectionCard
          label="File Index"
          actions={
            <Box component={RouterLink} to="?tab=files" sx={{ fontSize: 12.5, fontWeight: 500, color: c.tealInk2, textDecoration: "none" }}>
              Open files →
            </Box>
          }
          headerMb={14}
        >
          <Box sx={{ fontSize: 13, color: "#7a828a", lineHeight: 1.65 }}>
            {briefText ? briefText.slice(0, 320) : "No file index yet — /wrap writes the customer's file-index whenever a session changes files, so every /start knows what exists without loading it."}
          </Box>
        </SectionCard>
      </Box>

      {/* notes */}
      <SectionCard label="Notes">
        <Box sx={{ fontSize: 13.5, color: c.ink4, lineHeight: 1.7 }}>
          {customer.notes?.trim() ? customer.notes : <Box component="span" sx={{ color: c.muted2 }}>No notes yet.</Box>}
        </Box>
      </SectionCard>

      {!isAdmin && editMode && (
        <Box sx={{ fontSize: 12, color: c.warnText, mt: 1 }}>Note: saving a record requires admin — the server may refuse.</Box>
      )}
    </Box>
  );
}

function InstallField({ label, value, teal }: { label: string; value: string | null; teal?: boolean }) {
  return (
    <Box>
      <Box sx={{ fontSize: 12, color: c.muted2, mb: "6px" }}>{label}</Box>
      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 13, color: teal ? c.tealInk2 : c.ink, wordBreak: "break-all" }}>
        {value ?? "—"}
      </Box>
    </Box>
  );
}
